import type { FC, ReactNode } from 'react'
import type { ConsortiumSlug, SimulationSelection } from '@/types'

import { LockKeyhole, MessageCircle, ShieldCheck } from 'lucide-react'
import { SectionHeading } from '@/components/landing/section-heading'
import { LeadForm } from '@/components/landing/lead-form'
import { LEAD_FORM_SECTION_ID } from '@/hooks/use-proposal-selection'

interface LeadFormSectionProps {
    defaultSlug?: ConsortiumSlug
    title?: ReactNode
    description?: ReactNode
    selection: SimulationSelection | null
    onClearSelection: () => void
}

const assurances = [
    {
        icon: MessageCircle,
        title: 'Resposta pelo WhatsApp ou e-mail',
        description: 'Você recebe a proposta com crédito, prazo, parcela e as regras do grupo, tudo por escrito.'
    },
    {
        icon: ShieldCheck,
        title: 'Sem compromisso',
        description: 'Pedir uma proposta não gera contrato nem cobrança. Você só segue se fizer sentido.'
    },
    {
        icon: LockKeyhole,
        title: 'Seus dados protegidos',
        description: 'Usamos suas informações apenas para preparar a proposta, conforme a LGPD.'
    }
]

export const LeadFormSection: FC<LeadFormSectionProps> = ({
    defaultSlug,
    title = 'Receba uma proposta feita para o seu plano',
    description = 'Conte o que você quer conquistar. Um especialista analisa os grupos disponíveis e envia as opções que cabem no seu orçamento.',
    selection,
    onClearSelection
}): ReactNode => {
    return (
        <section
            id={LEAD_FORM_SECTION_ID}
            aria-labelledby='contato-titulo'
            className='relative overflow-hidden bg-sand py-24 sm:py-32'
        >
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-0 bg-dot-grid opacity-60 [mask-image:radial-gradient(ellipse_at_bottom_left,black,transparent_65%)]'
            />

            <div className='relative mx-auto grid max-w-7xl items-start gap-14 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20'>
                <div className='lg:sticky lg:top-28'>
                    <SectionHeading
                        id='contato-titulo'
                        eyebrow='Proposta sem compromisso'
                        title={title}
                        description={description}
                    />

                    <ul className='mt-10 grid gap-6'>
                        {assurances.map(item => (
                            <li key={item.title} className='flex items-start gap-4'>
                                <span className='grid size-11 shrink-0 place-items-center rounded-xl bg-card text-bronze shadow-sm ring-1 ring-border'>
                                    <item.icon aria-hidden='true' className='size-5' />
                                </span>

                                <div>
                                    <p className='font-semibold text-foreground'>
                                        {item.title}
                                    </p>

                                    <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                                        {item.description}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='rounded-[1.75rem] bg-card p-6 shadow-xl shadow-ink/5 ring-1 ring-border sm:p-10'>
                    <LeadForm defaultSlug={defaultSlug} selection={selection} onClearSelection={onClearSelection} />
                </div>
            </div>
        </section>
    )
}
